import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Home, Building2, Ruler, Paintbrush, Lightbulb, Package, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Services() {
  const services = [
    {
      icon: <Home className="h-8 w-8" />,
      title: 'Residential Design',
      description: 'Complete home interior solutions including living rooms, bedrooms, kitchens, and bathrooms. We create personalized spaces that reflect your lifestyle and preferences.',
      features: ['Space Planning', 'Custom Furniture', 'Color Consultation', '3D Visualization']
    },
    {
      icon: <Building2 className="h-8 w-8" />,
      title: 'Commercial Spaces',
      description: 'Transform offices, retail stores, restaurants, and hospitality venues into inspiring environments that enhance brand identity and customer experience.',
      features: ['Office Design', 'Retail Spaces', 'Restaurants & Cafes', 'Reception Areas']
    },
    {
      icon: <Ruler className="h-8 w-8" />,
      title: 'Space Planning',
      description: 'Optimize your available space with intelligent layout designs that maximize functionality while maintaining aesthetic appeal.',
      features: ['Layout Design', 'Traffic Flow', 'Furniture Placement', 'Storage Solutions']
    },
    {
      icon: <Paintbrush className="h-8 w-8" />,
      title: 'Custom Furniture',
      description: 'Bespoke furniture pieces designed and crafted to perfectly fit your space and requirements. From concept to installation.',
      features: ['Made-to-Measure', 'Material Selection', 'Craftsmanship', 'Installation']
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: 'Lighting Design',
      description: 'Strategic lighting solutions that enhance ambiance, highlight architectural features, and improve functionality.',
      features: ['Ambient Lighting', 'Task Lighting', 'Accent Lighting', 'Smart Controls']
    },
    {
      icon: <Package className="h-8 w-8" />,
      title: 'Turnkey Solutions',
      description: 'End-to-end project management from concept to completion. We handle everything so you can focus on what matters.',
      features: ['Project Management', 'Vendor Coordination', 'Quality Control', 'Timely Delivery']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We start with understanding your vision, lifestyle, and requirements through detailed consultation.'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Our team creates detailed design concepts, 3D visualizations, and material palettes for your approval.'
    },
    {
      step: '03',
      title: 'Execution',
      description: 'Expert craftsmen bring the design to life with precision, quality materials, and attention to detail.'
    },
    {
      step: '04',
      title: 'Delivery',
      description: 'Final installation, styling, and handover of your transformed space, ready to enjoy.'
    }
  ];

  return (
    <div className="py-16 md:py-24" data-testid="services-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
            What We Do
          </h2>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
            Our Services
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            From concept to completion, we offer comprehensive interior design services tailored to your unique needs and aspirations.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 h-full card-hover" data-testid={`service-detail-card-${index}`}>
                <div className="text-primary mb-4">{service.icon}</div>
                <h3 className="text-2xl font-semibold mb-4">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <span className="text-primary mr-2">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Process */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
              Our Process
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold">
              How We Work
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-6xl font-bold text-accent mb-4">{item.step}</div>
                <h4 className="text-xl font-semibold mb-3">{item.title}</h4>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                {index < process.length - 1 && (
                  <Separator className="hidden lg:block absolute top-8 -right-4 w-8" orientation="horizontal" />
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pricing Info */}
        <div className="mb-16">
          <Card className="p-8 md:p-12 bg-accent">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-semibold mb-4">Transparent Pricing</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Our pricing is based on the scope of work, square footage, and level of customization. We provide detailed quotes after the initial consultation.
                </p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span>No hidden fees or surprises</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span>Flexible payment plans available</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span>Free initial consultation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">✓</span>
                    <span>Competitive rates without compromising quality</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white/50 p-8 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Starting Prices</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b">
                    <span className="text-muted-foreground">Residential (per sq. ft.)</span>
                    <span className="font-semibold">₹80 - ₹150</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b">
                    <span className="text-muted-foreground">Commercial (per sq. ft.)</span>
                    <span className="font-semibold">₹100 - ₹200</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b">
                    <span className="text-muted-foreground">Consultation</span>
                    <span className="font-semibold">Free</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">3D Visualization</span>
                    <span className="font-semibold">₹5,000+</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  * Prices vary based on materials, finishes, and project complexity
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h3 className="text-3xl font-semibold mb-4">Ready to Get Started?</h3>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let's discuss your project and create a customized solution that fits your vision and budget.
          </p>
          <Link to="/contact">
            <Button size="lg" className="transition-colors duration-200" data-testid="services-cta-button">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}